import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ShieldCheck,
    Loader2,
    RefreshCw,
    Lock,
    Unlock,
    CheckCircle2,
    Save,
    Gift,
} from "lucide-react";
import { NeonBackground } from "@/components/reelflo/NeonBackground";
import { NeonLogo } from "@/components/reelflo/NeonLogo";
import { NeonButton } from "@/components/reelflo/NeonButton";
import { PasswordInput } from "@/components/reelflo/PasswordInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

const ADMIN_TOKEN_KEY = "reelflo_admin_token";
const FIELD_CLASS =
    "bg-white/[0.04] border-white/10 text-white placeholder:text-white/35 focus-visible:ring-0 focus-visible:shadow-[var(--rf-ring)] focus-visible:border-purple-400/40";

const Badge = ({ ok, label, okText, noText }) => (
    <span
        className={[
            "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
            ok
                ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200"
                : "border-white/15 bg-white/5 text-white/55",
        ].join(" ")}
    >
        {label}: {ok ? okText : noText}
    </span>
);

export default function Admin() {
    const navigate = useNavigate();
    const [token, setToken] = useState(
        () => localStorage.getItem(ADMIN_TOKEN_KEY) || "",
    );
    const [authed, setAuthed] = useState(false);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyEmail, setBusyEmail] = useState("");

    // WO#20 — comp (free-pilot) entitlement grant.
    const [comps, setComps] = useState([]);
    const [compEmail, setCompEmail] = useState("");
    const [compMinutes, setCompMinutes] = useState("");
    const [compReason, setCompReason] = useState("");
    const [compBusy, setCompBusy] = useState(false);

    const loadUsers = useCallback(
        async (tk) => {
            const useToken = tk ?? token;
            if (!useToken.trim()) {
                toast.error("Enter the admin token first.");
                return;
            }
            setLoading(true);
            try {
                const { data } = await api.get("/admin/users", {
                    headers: { "X-Admin-Token": useToken.trim() },
                });
                setUsers(data.users || []);
                setAuthed(true);
                localStorage.setItem(ADMIN_TOKEN_KEY, useToken.trim());
            } catch (err) {
                setAuthed(false);
                toast.error(
                    err?.response?.status === 403
                        ? "Invalid admin token."
                        : "Could not load users.",
                );
            } finally {
                setLoading(false);
            }
        },
        [token],
    );

    useEffect(() => {
        if (token) loadUsers(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // WO#20 — load recent comp grants for the audit list (admin-token gated).
    const loadComps = useCallback(
        async (tk) => {
            const useToken = (tk ?? token).trim();
            if (!useToken) return;
            try {
                const { data } = await api.get("/ledger/admin/comps?limit=10", {
                    headers: { "X-Admin-Token": useToken },
                });
                setComps(data.grants || []);
            } catch (err) {
                /* non-fatal — audit list is informational */
            }
        },
        [token],
    );

    useEffect(() => {
        if (authed) loadComps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authed]);

    // WO#20 — grant comp minutes + access (Stripe-independent).
    const grantComp = async () => {
        const email = compEmail.trim().toLowerCase();
        const minutes = parseInt(compMinutes, 10);
        const reason = compReason.trim();
        if (!email) {
            toast.error("Enter the user's email.");
            return;
        }
        if (!minutes || minutes <= 0) {
            toast.error("Enter minutes greater than 0.");
            return;
        }
        if (!reason) {
            toast.error("Enter a reason for the comp grant.");
            return;
        }
        setCompBusy(true);
        try {
            // Caller-supplied idempotency key (one per grant click). A network
            // re-delivery of this same request carries the same key, so the
            // backend will not double-grant.
            const compRequestId =
                (typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
            const { data } = await api.post(
                "/ledger/admin/comp",
                {
                    user_email: email,
                    minutes,
                    reason,
                    comp_request_id: compRequestId,
                },
                { headers: { "X-Admin-Token": token.trim() } },
            );
            toast.success(
                data.idempotent
                    ? `Already granted ${data.added_minutes} comp min to ${email} · ${data.balance.topup.available} min available`
                    : `Granted ${data.added_minutes} comp min to ${email} · ${data.balance.topup.available} min available`,
            );
            setCompEmail("");
            setCompMinutes("");
            setCompReason("");
            // Light in-place update of the affected row (avoids a heavy full
            // user-list reload that briefly destabilises the form). Refresh the
            // small comp-grants audit list.
            if (data.user) {
                setUsers((prev) =>
                    prev.map((u) =>
                        u.email === data.user.email ? { ...u, ...data.user } : u,
                    ),
                );
            }
            loadComps();
        } catch (err) {
            const status = err?.response?.status;
            toast.error(
                status === 403
                    ? "Invalid admin token."
                    : status === 404
                      ? "User not found for that email."
                      : status === 409
                        ? err?.response?.data?.detail ||
                          "Idempotency key already used for a different grant."
                        : err?.response?.data?.detail || "Comp grant failed.",
            );
        } finally {
            setCompBusy(false);
        }
    };

    const setReady = async (email, account_ready) => {
        setBusyEmail(email);
        try {
            const { data } = await api.post(
                "/admin/set-ready",
                { email, account_ready },
                { headers: { "X-Admin-Token": token.trim() } },
            );
            const updated = data.user;
            setUsers((prev) =>
                prev.map((u) => (u.email === email ? { ...u, ...updated } : u)),
            );
            toast.success(
                account_ready
                    ? `${email} unlocked (ready).`
                    : `${email} locked.`,
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed.");
        } finally {
            setBusyEmail("");
        }
    };

    // Per-user draft values for the Submagic Preset ID input.
    const [presetDrafts, setPresetDrafts] = useState({});
    const [presetBusy, setPresetBusy] = useState("");

    const draftFor = (u) =>
        presetDrafts[u.email] !== undefined
            ? presetDrafts[u.email]
            : u.submagic_preset_id || "";

    const savePreset = async (email) => {
        setPresetBusy(email);
        try {
            const value = (presetDrafts[email] ?? "").trim();
            const { data } = await api.post(
                "/admin/set-preset",
                { email, submagic_preset_id: value },
                { headers: { "X-Admin-Token": token.trim() } },
            );
            const updated = data.user;
            setUsers((prev) =>
                prev.map((u) => (u.email === email ? { ...u, ...updated } : u)),
            );
            setPresetDrafts((prev) => {
                const next = { ...prev };
                delete next[email];
                return next;
            });
            toast.success(
                value
                    ? `Preset saved for ${email}.`
                    : `Preset cleared for ${email}.`,
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save preset.");
        } finally {
            setPresetBusy("");
        }
    };

    return (
        <div className="relative min-h-screen overflow-hidden bg-black text-white">
            <NeonBackground intensity={0.5} />

            <header className="relative z-20 mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-5 sm:px-6">
                <NeonLogo onClick={() => navigate("/")} />
                <span className="inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-purple-500/10 px-4 py-2 text-sm font-semibold text-purple-200">
                    <ShieldCheck className="h-4 w-4" /> Admin
                </span>
            </header>

            <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
                {!authed ? (
                    <div className="mx-auto mt-10 max-w-md rounded-3xl border border-white/12 bg-white/[0.03] p-7 backdrop-blur sm:p-9">
                        <h1 className="rf-heading text-2xl font-bold">Admin access</h1>
                        <p className="mt-2 text-sm text-white/55">
                            Enter the admin token to manage account activation.
                        </p>
                        <div className="mt-5 space-y-1.5">
                            <Label htmlFor="admin-token" className="text-white/80">
                                Admin token
                            </Label>
                            <PasswordInput
                                id="admin-token"
                                data-testid="admin-token-input"
                                toggleTestId="admin-token-toggle"
                                autoComplete="off"
                                className={FIELD_CLASS}
                                placeholder="X-Admin-Token"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && loadUsers()}
                            />
                        </div>
                        <NeonButton
                            size="block"
                            className="mt-5"
                            data-testid="admin-login-button"
                            disabled={loading}
                            onClick={() => loadUsers()}
                        >
                            {loading ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                                <ShieldCheck className="h-5 w-5" />
                            )}
                            Unlock admin
                        </NeonButton>
                    </div>
                ) : (
                    <>
                        {/* WO#20 — Free-pilot comp entitlement (Stripe-independent) */}
                        <div
                            data-testid="comp-grant-panel"
                            className="mb-6 rounded-3xl border border-purple-400/20 bg-purple-500/[0.04] p-6 backdrop-blur sm:p-7"
                        >
                            <div className="flex items-center gap-2">
                                <Gift className="h-5 w-5 text-purple-300" />
                                <h2 className="rf-heading text-xl font-bold">
                                    Grant comp minutes
                                </h2>
                            </div>
                            <p className="mt-1 text-sm text-white/50">
                                Free-pilot entitlement — adds standalone minutes and
                                unlocks dashboard access. No Stripe subscription required.
                            </p>

                            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-[1.5fr_0.7fr_1.5fr_auto] lg:items-end">
                                <div className="space-y-1.5">
                                    <Label
                                        htmlFor="comp-email"
                                        className="text-white/80"
                                    >
                                        User email
                                    </Label>
                                    <Input
                                        id="comp-email"
                                        data-testid="comp-email-input"
                                        type="email"
                                        autoComplete="off"
                                        placeholder="user@example.com"
                                        className={FIELD_CLASS}
                                        value={compEmail}
                                        onChange={(e) =>
                                            setCompEmail(e.target.value)
                                        }
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label
                                        htmlFor="comp-minutes"
                                        className="text-white/80"
                                    >
                                        Minutes
                                    </Label>
                                    <Input
                                        id="comp-minutes"
                                        data-testid="comp-minutes-input"
                                        type="number"
                                        min="1"
                                        placeholder="30"
                                        className={FIELD_CLASS}
                                        value={compMinutes}
                                        onChange={(e) =>
                                            setCompMinutes(e.target.value)
                                        }
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label
                                        htmlFor="comp-reason"
                                        className="text-white/80"
                                    >
                                        Reason
                                    </Label>
                                    <Input
                                        id="comp-reason"
                                        data-testid="comp-reason-input"
                                        type="text"
                                        autoComplete="off"
                                        placeholder="Free pilot — Q3 partner"
                                        className={FIELD_CLASS}
                                        value={compReason}
                                        onChange={(e) =>
                                            setCompReason(e.target.value)
                                        }
                                        onKeyDown={(e) =>
                                            e.key === "Enter" && grantComp()
                                        }
                                    />
                                </div>
                                <NeonButton
                                    size="md"
                                    data-testid="comp-grant-button"
                                    disabled={compBusy}
                                    onClick={grantComp}
                                >
                                    {compBusy ? (
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                    ) : (
                                        <Gift className="h-5 w-5" />
                                    )}
                                    Grant comp
                                </NeonButton>
                            </div>

                            {comps.length > 0 ? (
                                <div
                                    data-testid="comp-recent-list"
                                    className="mt-5 border-t border-white/10 pt-4"
                                >
                                    <p className="text-xs font-medium uppercase tracking-wide text-white/45">
                                        Recent comp grants
                                    </p>
                                    <div className="mt-2 space-y-1.5">
                                        {comps.map((g) => (
                                            <div
                                                key={g.id}
                                                data-testid={`comp-grant-row-${g.id}`}
                                                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm"
                                            >
                                                <span className="font-medium text-white/85">
                                                    {g.user_email}
                                                </span>
                                                <span className="inline-flex items-center gap-2 text-white/55">
                                                    <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-200">
                                                        +{g.minutes} min
                                                    </span>
                                                    {g.reason ? (
                                                        <span className="max-w-[16rem] truncate">
                                                            {g.reason}
                                                        </span>
                                                    ) : null}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="mb-5 flex items-center justify-between gap-3">
                            <div>
                                <h1 className="rf-heading text-3xl font-bold">
                                    Client accounts
                                </h1>
                                <p className="text-sm text-white/45">
                                    {users.length} user
                                    {users.length === 1 ? "" : "s"}
                                </p>
                            </div>
                            <button
                                type="button"
                                data-testid="admin-refresh-button"
                                onClick={() => loadUsers()}
                                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white"
                            >
                                <RefreshCw
                                    className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                                />
                                Refresh
                            </button>
                        </div>

                        <div className="space-y-3">
                            {users.map((u) => {
                                const isReady = u.account_ready !== false;
                                const busy = busyEmail === u.email;
                                return (
                                    <div
                                        key={u.email}
                                        data-testid={`admin-user-row-${u.email}`}
                                        className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5"
                                    >
                                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                        <div className="min-w-0">
                                            <p className="truncate text-base font-semibold text-white">
                                                {u.full_name || "—"}
                                            </p>
                                            <p className="truncate text-sm text-white/55">
                                                {u.email}
                                            </p>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                <span
                                                    data-testid={`admin-ready-badge-${u.email}`}
                                                    className={[
                                                        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold",
                                                        isReady
                                                            ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200"
                                                            : "border-amber-400/30 bg-amber-500/10 text-amber-200",
                                                    ].join(" ")}
                                                >
                                                    {isReady ? (
                                                        <Unlock className="h-3 w-3" />
                                                    ) : (
                                                        <Lock className="h-3 w-3" />
                                                    )}
                                                    {isReady ? "Ready" : "Locked"}
                                                </span>
                                                <Badge
                                                    ok={
                                                        u.subscription_status ===
                                                            "active" ||
                                                        u.activated
                                                    }
                                                    label="Sub"
                                                    okText={
                                                        u.subscription_status ||
                                                        "active"
                                                    }
                                                    noText="none"
                                                />
                                                <Badge
                                                    ok={u.submagic_api_key_set}
                                                    label="Submagic"
                                                    okText="set"
                                                    noText="no"
                                                />
                                                <Badge
                                                    ok={u.google_connected}
                                                    label="Google"
                                                    okText="linked"
                                                    noText="no"
                                                />
                                                <span
                                                    data-testid={`admin-preset-badge-${u.email}`}
                                                    className={[
                                                        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
                                                        u.submagic_preset_id
                                                            ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200"
                                                            : "border-white/15 bg-white/5 text-white/55",
                                                    ].join(" ")}
                                                >
                                                    Preset:{" "}
                                                    {u.submagic_preset_id
                                                        ? "Set"
                                                        : "Not set yet"}
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            data-testid={`admin-toggle-ready-${u.email}`}
                                            disabled={busy}
                                            onClick={() =>
                                                setReady(u.email, !isReady)
                                            }
                                            className={[
                                                "inline-flex flex-none items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60",
                                                isReady
                                                    ? "border border-amber-400/30 bg-amber-500/10 text-amber-200 hover:border-amber-300/50 hover:bg-amber-500/20"
                                                    : "border border-emerald-400/40 bg-emerald-500/15 text-emerald-100 hover:border-emerald-300/60 hover:bg-emerald-500/25",
                                            ].join(" ")}
                                        >
                                            {busy ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : isReady ? (
                                                <Lock className="h-4 w-4" />
                                            ) : (
                                                <CheckCircle2 className="h-4 w-4" />
                                            )}
                                            {isReady ? "Lock account" : "Set Ready"}
                                        </button>
                                        </div>

                                        {/* Submagic Preset ID — admin-only, optional, storage-only */}
                                        <div className="border-t border-white/10 pt-4">
                                            <label
                                                htmlFor={`preset-${u.email}`}
                                                className="text-xs font-medium uppercase tracking-wide text-white/45"
                                            >
                                                Submagic Preset ID
                                            </label>
                                            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                                                <input
                                                    id={`preset-${u.email}`}
                                                    data-testid={`admin-preset-input-${u.email}`}
                                                    type="text"
                                                    autoComplete="off"
                                                    spellCheck={false}
                                                    value={draftFor(u)}
                                                    onChange={(e) =>
                                                        setPresetDrafts((p) => ({
                                                            ...p,
                                                            [u.email]: e.target.value,
                                                        }))
                                                    }
                                                    placeholder="Not set yet — paste preset ID"
                                                    className="w-full flex-1 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white placeholder:text-white/35 focus-visible:border-purple-400/40 focus-visible:outline-none"
                                                />
                                                <button
                                                    type="button"
                                                    data-testid={`admin-preset-save-${u.email}`}
                                                    disabled={presetBusy === u.email}
                                                    onClick={() => savePreset(u.email)}
                                                    className="inline-flex flex-none items-center justify-center gap-2 rounded-lg border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-200 transition-colors hover:border-cyan-300/50 hover:bg-cyan-500/20 disabled:opacity-60"
                                                >
                                                    {presetBusy === u.email ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Save className="h-4 w-4" />
                                                    )}
                                                    Save
                                                </button>
                                            </div>
                                            {!u.submagic_preset_id ? (
                                                <p
                                                    data-testid={`admin-preset-notset-${u.email}`}
                                                    className="mt-1.5 text-xs text-white/40"
                                                >
                                                    Not set yet · pending onboarding
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
